import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import '../styles/Login.css';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Password reset email sent!');
    } catch (error) {
      // We show success even on error to prevent email enumeration
      setSubmitted(true);
      toast.success('If this email exists, a reset link has been sent.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="login-page" data-testid="forgot-password-page">
        <div className="login-container fade-in">
          <div className="login-brand" data-testid="forgot-password-brand">
            <div className="brand-content">
              <h1 className="brand-title" style={{ color: '#E7EDF9' }}>CNESS Hive</h1>
              <p className="brand-tagline">Your productivity partner for balanced excellence</p>
            </div>
          </div>

          <div className="login-form-container">
            <div className="login-form-content" style={{ textAlign: 'center' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <CheckCircle size={40} color="white" />
              </div>
              
              <h2 style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>Check Your Email</h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', lineHeight: '1.6' }}>
                If an account exists for <strong>{email}</strong>, you&apos;ll receive a password reset link shortly.
              </p>
              <p style={{ color: 'var(--text-tertiary)', fontSize: '14px', marginBottom: '32px' }}>
                The link will expire in 24 hours. Don&apos;t forget to check your spam folder!
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Link 
                  to="/login" 
                  className="gradient-btn"
                  style={{ 
                    textDecoration: 'none', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '8px',
                    padding: '14px 24px'
                  }}
                >
                  <ArrowLeft size={18} />
                  Back to Login
                </Link>
                
                <button
                  onClick={() => { setSubmitted(false); setEmail(''); }}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border-color)',
                    padding: '14px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                    fontSize: '14px'
                  }}
                >
                  Try a different email
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" data-testid="forgot-password-page">
      <div className="login-container fade-in">
        <div className="login-brand" data-testid="forgot-password-brand">
          <div className="brand-content">
            <h1 className="brand-title" style={{ color: '#E7EDF9' }}>CNESS Hive</h1>
            <p className="brand-tagline">Your productivity partner for balanced excellence</p>
            <div className="brand-features">
              <div className="brand-feature">
                <div className="feature-dot"></div>
                <span>Secure password recovery</span>
              </div>
              <div className="brand-feature">
                <div className="feature-dot"></div>
                <span>Email verification</span>
              </div>
              <div className="brand-feature">
                <div className="feature-dot"></div>
                <span>24-hour reset window</span>
              </div>
            </div>
          </div>
        </div>

        <div className="login-form-container">
          <div className="login-form-content">
            <div className="form-header">
              <h2>Forgot Password?</h2>
              <p>Enter your email and we&apos;ll send you a reset link</p>
            </div>

            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your registered email"
                    required
                    data-testid="forgot-password-email"
                    style={{ paddingLeft: '44px' }}
                  />
                  <Mail 
                    size={18} 
                    style={{ 
                      position: 'absolute', 
                      left: '14px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      color: 'var(--text-tertiary)'
                    }} 
                  />
                </div>
              </div>

              <button
                type="submit"
                className="login-btn gradient-btn"
                disabled={loading}
                data-testid="forgot-password-submit"
              >
                {loading ? (
                  <span>Sending...</span>
                ) : (
                  <>
                    <Send size={18} />
                    <span>Send Reset Link</span>
                  </>
                )}
              </button>
            </form>

            <div className="form-footer">
              <Link to="/login" className="back-link">← Back to Login</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
